import React, { useEffect, useState } from "react";
import "./Reportscraft.css";

const Reportscraft = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [selectedItem, setSelectedItem] = useState("All");

    const userEmail = sessionStorage.getItem("userEmail");

    // ----------------------------------
    // GET REPORT DATA
    // ----------------------------------

    const fetchReports = async () => {

        if (!userEmail) {
            console.error("User email not found");
            setLoading(false);
            return;
        }

        try {
            setLoading(true);

            const response = await fetch(
                `/inventory?gmail=${encodeURIComponent(userEmail)}`
            );

            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }

            const data = await response.json();

            setReports(data);

        } catch (error) {
            console.error("Error fetching report:", error);
            setReports([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, [userEmail]);


    // ----------------------------------
    // GET UNIQUE ITEMS
    // ----------------------------------

    const items = [
        "All",
        ...new Set(
            reports
                .map(row => row.item)
                .filter(Boolean)
        )
    ];


    // ----------------------------------
    // FILTER REPORT
    // ----------------------------------

    const filteredReports = reports.filter(row => {

        const rowDate = row.date
            ? String(row.date).substring(0, 10)
            : "";

        if (fromDate && rowDate < fromDate) {
            return false;
        }

        if (toDate && rowDate > toDate) {
            return false;
        }

        if (
            selectedItem !== "All" &&
            row.item !== selectedItem
        ) {
            return false;
        }

        return true;
    });


    // ----------------------------------
    // SORT BY DATE
    // ----------------------------------

    const sortedReports = [...filteredReports].sort(
        (a, b) =>
            new Date(a.date) - new Date(b.date)
    );


    // ----------------------------------
    // CALCULATE TOTALS
    // ----------------------------------

    const totalInwards = sortedReports.reduce(
        (total, row) =>
            total + Number(row.inwards || 0),
        0
    );

    const totalOutwards = sortedReports.reduce(
        (total, row) =>
            total + Number(row.outwards || 0),
        0
    );

    const balance = totalInwards - totalOutwards;


    // ----------------------------------
    // FORMAT NUMBER
    // ----------------------------------

    const formatNumber = (value) => {
        return Number(value || 0).toLocaleString(
            "en-IN",
            {
                maximumFractionDigits: 2
            }
        );
    };


    // ----------------------------------
    // CLEAR FILTERS
    // ----------------------------------

    const clearFilters = () => {
        setFromDate("");
        setToDate("");
        setSelectedItem("All");
    };


    return (
        <div className="reports-page">

            {/* HEADER */}
            <div className="reports-header">

                <div>
                    <h1>Inventory Reports</h1>

                    <p>
                        Track inventory movement and balance
                    </p>
                </div>

            </div>


            {/* FILTERS 
            <div className="reports-filters">

                <div className="filter-group">
                    <label>From Date</label>

                    <input
                        type="date"
                        value={fromDate}
                        onChange={(e) =>
                            setFromDate(e.target.value)
                        }
                    />
                </div>


                <div className="filter-group">
                    <label>To Date</label>

                    <input
                        type="date"
                        value={toDate}
                        onChange={(e) =>
                            setToDate(e.target.value)
                        }
                    />
                </div>


                <div className="filter-group">
                    <label>Item</label>

                    <select
                        value={selectedItem}
                        onChange={(e) =>
                            setSelectedItem(e.target.value)
                        }
                    >
                        {items.map((item) => (
                            <option
                                key={item}
                                value={item}
                            >
                                {item}
                            </option>
                        ))}
                    </select>
                </div>


                <button
                    className="clear-filter-btn"
                    onClick={clearFilters}
                >
                    Clear
                </button>

            </div>     */}


            {/* SUMMARY CARDS */}
            <div className="report-summary">

                <div className="summary-card inward-card">
                    <span className="summary-label">
                        Total Inwards
                    </span>

                    <strong>
                        {formatNumber(totalInwards)}
                    </strong>

                    <small>
                        Stock received
                    </small>
                </div>


                <div className="summary-card outward-card">
                    <span className="summary-label">
                        Total Outwards
                    </span>

                    <strong>
                        {formatNumber(totalOutwards)}
                    </strong>

                    <small>
                        Stock issued
                    </small>
                </div>


                <div className="summary-card balance-card">
                    <span className="summary-label">
                        Balance
                    </span>

                    <strong>
                        {formatNumber(balance)}
                    </strong>

                    <small>
                        Remaining stock
                    </small>
                </div>

            </div>


            {/* REPORT TABLE */}
            <div className="reports-table-container">

                <div className="table-title">
                    <h2>Stock Movement</h2>

                    <span>
                        {sortedReports.length} records
                    </span>
                </div>


                {loading ? (

                    <div className="report-message">
                        Loading report...
                    </div>

                ) : sortedReports.length === 0 ? (

                    <div className="report-message">
                        No inventory records found.
                    </div>

                ) : (

                    <table className="reports-table">

                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Item</th>
                                <th>Vendor</th>
                                <th>Inwards</th>
                                <th>Outwards</th>
                                <th>Balance</th>
                            </tr>
                        </thead>


                        <tbody>

                            {sortedReports.map((row, index) => {

                                /*
                                 * Balance for this row:
                                 * Inwards - Outwards
                                 *
                                 * The cumulative balance is
                                 * calculated below.
                                 */

                                const previousRows =
                                    sortedReports.slice(0, index);

                                const previousBalance =
                                    previousRows.reduce(
                                        (total, previousRow) =>
                                            total +
                                            Number(
                                                previousRow.inwards || 0
                                            ) -
                                            Number(
                                                previousRow.outwards || 0
                                            ),
                                        0
                                    );

                                const currentBalance =
                                    previousBalance +
                                    Number(row.inwards || 0) -
                                    Number(row.outwards || 0);

                                return (
                                    <tr key={row.inventory_id}>

                                        <td>
                                            {row.date
                                                ? String(row.date).substring(0, 10)
                                                : "-"}
                                        </td>

                                        <td className="item-name">
                                            {row.item}
                                        </td>

                                        <td>
                                            {row.vendor_name || "-"}
                                        </td>

                                        <td className="inward-value">
                                            {formatNumber(row.inwards)}
                                        </td>

                                        <td className="outward-value">
                                            {formatNumber(row.outwards)}
                                        </td>

                                        <td className="balance-value">
                                            {formatNumber(currentBalance)}
                                        </td>

                                    </tr>
                                );
                            })}

                        </tbody>

                    </table>

                )}

            </div>

        </div>
    );
};

export default Reportscraft;